/**
 * ProcessingStateTimeline Component
 * A horizontal progress indicator showing the tender's journey through the pipeline
 */

import { CheckIcon } from '@heroicons/react/24/solid';
import {
    PROCESSING_STATES,
    MAIN_FLOW_STATES,
    getStateInfo,
    isProcessingState,
    getStateOrder,
} from '../utils/processingState';

const ProcessingStateTimeline = ({
    currentState,
    stateChangedAt,
    onStateClick,
    compact = false,
    showEnrichmentBranch = false,
}) => {
    const currentOrder = getStateOrder(currentState);
    const isError = currentState === 'ERROR';
    const isArchived = currentState === 'ARCHIVED';

    // Check if enrichment path was taken
    const enrichmentTaken = ['ENRICHING', 'ENRICHED'].includes(currentState) ||
        (currentOrder > getStateOrder('ENRICHED') && showEnrichmentBranch);

    // Build the flow to display
    const flowStates = enrichmentTaken
        ? ['DRAFT', 'DISCOVERED', 'CRAWLING', 'CRAWLED', 'ENRICHING', 'ENRICHED', 'PROCESSING', 'PROCESSED', 'ASSESSING', 'ASSESSED']
        : MAIN_FLOW_STATES;

    const getStepStatus = (state) => {
        if (isError || isArchived) {
            // For error/archived, show all as incomplete
            const stateOrder = getStateOrder(state);
            if (stateOrder < currentOrder) return 'completed';
            return 'upcoming';
        }

        const stateOrder = getStateOrder(state);
        if (state === currentState) return 'current';
        if (stateOrder < currentOrder) return 'completed';
        return 'upcoming';
    };

    if (compact) {
        // Compact version for inline display
        return (
            <div className="flex items-center gap-1">
                {flowStates.map((state, index) => {
                    const status = getStepStatus(state);
                    const info = getStateInfo(state);
                    const Icon = info.Icon;
                    const isCurrent = state === currentState;
                    const isInProgress = isCurrent && isProcessingState(state);

                    return (
                        <div key={state} className="flex items-center">
                            {/* Step indicator */}
                            <div
                                className={`
 flex items-center justify-center rounded-full transition-all
 ${isCurrent ? 'w-6 h-6' : 'w-4 h-4'}
 ${status === 'completed' ? 'bg-green-600' : ''}
 ${status === 'current' ? `${info.bgClass} ring-2 ${info.ringClass}` : ''}
                                    ${status === 'upcoming' ? 'bg-gray-700 border border-gray-600' : ''}
                                    ${isInProgress ? 'animate-pulse' : ''}
                                `}
                                title={info.label}
                            >
                                {status === 'completed' && (
                                    <CheckIcon className="w-2.5 h-2.5 text-white" />
                                )}
                                {status === 'current' && (
                                    <Icon className={`w-3 h-3 ${info.textClass} ${isInProgress ? 'animate-spin' : ''}`} />
                                )}
                            </div>

                            {/* Connector line */}
                            {index < flowStates.length - 1 && (
                                <div className={`w-2 h-0.5 ${status === 'completed' || status === 'current' ? 'bg-green-600' : 'bg-gray-700'}`} />
                            )}
                        </div>
                    );
                })}

                {/* Error indicator */}
                {isError && (
                    <div className="ml-2 px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded-full">
                        Error
                    </div>
                )}
            </div>
        );
    }

    // Full timeline version
    return (
        <div className="w-full">
            {/* Timeline header */}
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-300">Pipeline Progress</h4>
                {stateChangedAt && (
                    <span className="text-xs text-gray-500">
                        Last updated: {new Date(stateChangedAt).toLocaleString()}
                    </span>
                )}
            </div>

            {/* Timeline */}
            <div className="relative">
                {/* Background line */}
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-700" />

                {/* Progress line */}
                <div
                    className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-green-600 to-green-500 transition-all duration-500"
                    style={{
                        width: `${Math.min(100, (currentOrder / (flowStates.length - 1)) * 100)}%`
                    }}
                />

                {/* Steps */}
                <div className="relative flex justify-between">
                    {flowStates.map((state) => {
                        const status = getStepStatus(state);
                        const info = getStateInfo(state);
                        const Icon = info.Icon;
                        const isCurrent = state === currentState;
                        const isInProgress = isCurrent && isProcessingState(state);
                        const canClick = onStateClick && status !== 'upcoming' && !isProcessingState(state);

                        return (
                            <div
                                key={state}
                                className={`flex flex-col items-center ${canClick ? 'cursor-pointer' : ''}`}
                                onClick={() => canClick && onStateClick(state)}
                            >
                                {/* Step circle */}
                                <div
                                    className={`
 flex items-center justify-center rounded-full transition-all z-10
 ${isCurrent ? 'w-10 h-10' : 'w-8 h-8'}
 ${status === 'completed' ? 'bg-green-600 hover:bg-green-500' : ''}
 ${status === 'current' ? `${info.bgClass} ring-4 ${info.ringClass}/30` : ''}
                                        ${status === 'upcoming' ? 'bg-gray-800 border-2 border-gray-600' : ''}
                                        ${isInProgress ? 'animate-pulse' : ''}
                                        ${canClick ? 'hover:scale-110' : ''}
                                    `}
                                >
                                    {status === 'completed' && (
                                        <CheckIcon className="w-4 h-4 text-white" />
                                    )}
                                    {status === 'current' && (
                                        <Icon className={`w-5 h-5 ${info.textClass} ${isInProgress ? 'animate-spin' : ''}`} />
                                    )}
                                    {status === 'upcoming' && (
                                        <Icon className="w-4 h-4 text-gray-500" />
                                    )}
                                </div>

                                {/* Label */}
                                <span
                                    className={`
 mt-2 text-xs font-medium text-center
 ${status === 'current' ? info.textClass : ''}
 ${status === 'completed' ? 'text-green-700' : ''}
 ${status === 'upcoming' ? 'text-gray-500' : ''}
 `}
                                >
                                    {info.label}
                                </span>

                                {/* Current indicator */}
                                {isCurrent && (
                                    <div className={`mt-1 px-2 py-0.5 ${info.bgClass} ${info.textClass} text-xs rounded-full`}>
                                        Current
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Error/Archived indicator */}
            {(isError || isArchived) && (
                <div className={`
 mt-6 p-3 flex items-center gap-3
 ${isError ? 'bg-red-50 border border-red-200' : 'bg-gray-800/50 border border-gray-700'}
 `}>
                    {isError ? (
                        <>
                            <PROCESSING_STATES.ERROR.Icon className="w-5 h-5 text-red-700" />
                            <div>
                                <div className="text-sm font-medium text-red-700">Error State</div>
                                <div className="text-xs text-red-700/70">
                                    This tender encountered an error and may require manual intervention.
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <PROCESSING_STATES.ARCHIVED.Icon className="w-5 h-5 text-gray-400" />
                            <div>
                                <div className="text-sm font-medium text-gray-400">Archived</div>
                                <div className="text-xs text-gray-500">
                                    Deadline has passed. This tender is no longer active.
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Enrichment branch indicator */}
            {enrichmentTaken && (
                <div className="mt-4 flex items-center gap-2 text-xs text-cyan-700">
                    <PROCESSING_STATES.ENRICHING.Icon className="w-4 h-4" />
                    <span>Deep crawl enrichment was applied</span>
                </div>
            )}
        </div>
    );
};

export default ProcessingStateTimeline;
